// ✅ Trekkin.app — useCan.js v2.2.0 (STRICT + SAFE)
// -----------------------------------------------------------------------------
// 🔐 Verificação central de permissões
// 🔹 Fonte única: useAuthStore.permissions
// 🔹 Admin = acesso total
// 🔹 NENHUM fallback permissivo
// -----------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { get, ref } from "firebase/database";
import { db } from "../firebaseConfig";
import { useAuthStore } from "../stores/useAuthStore";
import { useGroupStore } from "../stores/useGroupStore";
import { useEventsStore } from "../stores/useEventsStore";

export function useCan(panelId, fnId) {
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const permissionsLoaded = useAuthStore((s) => s.permissionsLoaded);
  const offline = useAuthStore((s) => s.offline);
  const currentGroup = useGroupStore((s) => s.currentGroup);
  const groups = useGroupStore((s) => s.groups);
  const isEventLive = useEventsStore((s) => s.isEventLive);
  const [adminPreview, setAdminPreview] = useState(() => {
    try {
      const enabled = localStorage.getItem("trekkin_admin_user_view_v1") === "1";
      const role = String(localStorage.getItem("trekkin_admin_user_view_role_v1") || "free").toLowerCase();
      return { enabled, role, permissions: null };
    } catch {
      return { enabled: false, role: "free", permissions: null };
    }
  });

  const role = user?.role?.toLowerCase() || null;
  const isAdminAccount = role === "admin";
  const isAdmin = isAdminAccount && !adminPreview.enabled;

  useEffect(() => {
    if (!isAdminAccount) return undefined;
    let active = true;
    const sync = async (event) => {
      let enabled;
      let previewRole;
      try {
        enabled = typeof event?.detail?.enabled === "boolean"
          ? event.detail.enabled
          : localStorage.getItem("trekkin_admin_user_view_v1") === "1";
        previewRole = String(
          event?.detail?.role || localStorage.getItem("trekkin_admin_user_view_role_v1") || "free"
        ).toLowerCase();
      } catch {
        enabled = false;
        previewRole = "free";
      }
      if (!enabled) {
        if (active) setAdminPreview({ enabled: false, role: previewRole, permissions: null });
        return;
      }
      if (active) setAdminPreview((current) => ({ ...current, enabled: true, role: previewRole, permissions: null }));
      try {
        const snapshot = await get(ref(db, `/permissions/${previewRole}`));
        if (active) setAdminPreview({ enabled: true, role: previewRole, permissions: snapshot.val() || {} });
      } catch {
        if (active) setAdminPreview({ enabled: true, role: previewRole, permissions: {} });
      }
    };
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener("trekkin:admin-user-view", sync);
    return () => {
      active = false;
      window.removeEventListener("storage", sync);
      window.removeEventListener("trekkin:admin-user-view", sync);
    };
  }, [isAdminAccount]);

  const resolveGroup = () => {
    if (currentGroup && typeof currentGroup === "object") return currentGroup;
    const gid =
      typeof currentGroup === "string"
        ? currentGroup
        : currentGroup?.id || currentGroup?.groupId || currentGroup?.firebaseId || null;
    if (!gid) return null;
    return (
      (groups || []).find(
        (g) =>
          g.id === gid ||
          g.groupId === gid ||
          g.firebaseId === gid ||
          g.key === gid
      ) || null
    );
  };

  const canBypassWaypoints = () => {
    if (!user?.uid) return false;
    const group = resolveGroup();
    const activeEventId =
      group?.activeEventId || group?.activeEvent || group?.eventId || null;
    if (!activeEventId || !isEventLive(activeEventId)) return false;
    return !!group?.members?.[user.uid];
  };

  const check = (panel, fn) => {
    // ❌ Sem sessão válida
    if (!user && !offline) return false;

    // 🔑 Admin manda em tudo
    if (isAdmin) return true;

    // ❌ Chamada inválida
    if (!panel || !fn) return false;

    if (
      panel === "PanelWaypoints" &&
      (fn === "open" || fn === "filter" || fn === "navigate") &&
      canBypassWaypoints()
    ) {
      return true;
    }

    // ⏳ Ainda a carregar permissões (online)
    if (!permissionsLoaded && !offline) return false;

    const effectivePermissions = isAdminAccount && adminPreview.enabled
      ? adminPreview.permissions
      : permissions;
    const value = effectivePermissions?.[panel]?.[fn];

    // STRICT MODE
    return value === true;
  };

  if (panelId && fnId) {
    return check(panelId, fnId);
  }

  return check;
}
